"use client";

import { LockedOverlay } from "./LockedOverlay";
import { FakeDataWatermark } from "./FakeDataWatermark";

const HEADERS = ["#", "Image", "Name", "Release", "CCU", "Top", "Price", "Platforms"];

const FAKE_ROWS = [
  { rank: 1, name: "Phantom Odyssey", release: "Mar 12, 2025", ccu: "12,450", top: "#3", price: "$29.99" },
  { rank: 2, name: "Stellar Vanguard", release: "Jan 8, 2025", ccu: "8,720", top: "#7", price: "$39.99" },
  { rank: 3, name: "Crimson Protocol", release: "Apr 22, 2024", ccu: "5,430", top: "#12", price: "$19.99" },
  { rank: 4, name: "Neon Abyss Zero", release: "Feb 15, 2025", ccu: "24,100", top: "#1", price: "Free" },
  { rank: 5, name: "Ironclad Legacy", release: "Nov 4, 2024", ccu: "3,890", top: "#18", price: "$49.99" },
  { rank: 6, name: "Void Breakers", release: "Jul 18, 2024", ccu: "2,540", top: "#25", price: "$24.99" },
  { rank: 7, name: "Arctic Siege II", release: "Sep 2, 2024", ccu: "1,980", top: "#31", price: "$34.99" },
  { rank: 8, name: "Drift Horizon", release: "Dec 10, 2024", ccu: "1,650", top: "#42", price: "$14.99" },
  { rank: 9, name: "Eclipse Frontier", release: "Oct 28, 2024", ccu: "1,220", top: "#55", price: "$44.99" },
  { rank: 10, name: "Shadow Meridian", release: "Aug 14, 2024", ccu: "980", top: "#68", price: "$29.99" },
];

interface LockedMonthlyTableProps {
  /** Overlay CTA — /prerelease reuses this fake table with its own text. */
  message?: string;
  /** Analytics surface for the "lock viewed" impression (per page). */
  surface?: string;
}

export function LockedMonthlyTable({
  message = "Sign in to see Game Releases",
  surface,
}: LockedMonthlyTableProps) {
  return (
    <div data-nosnippet className="bg-[#16202d] rounded-lg border border-[#2a475e] overflow-hidden relative">
      <div className="select-none blur-[6px] pointer-events-none relative">
        {/* Header */}
        <div className="hidden md:flex items-center gap-2 px-2 py-1.5 h-8 border-b border-[#2a475e] bg-[#1b2838] text-[#c7d5e0] text-[11px] font-semibold uppercase tracking-wide">
          {HEADERS.map((h) => (
            <div
              key={h}
              className={
                h === "Name"
                  ? "flex-1 min-w-0"
                  : h === "Image"
                    ? "w-[100px] flex-shrink-0"
                    : "w-[64px] flex-shrink-0 text-right"
              }
            >
              {h}
            </div>
          ))}
        </div>

        {/* Data rows */}
        {FAKE_ROWS.map((row, i) => (
          <div
            key={row.rank}
            className={`hidden md:flex items-center gap-2 border-b border-[#2a475e] ${
              i % 2 === 1 ? "bg-[#15212f]" : ""
            }`}
          >
            <div className="w-[64px] flex-shrink-0 px-2 text-right font-bold text-[13px] text-[#66c0f4]">
              #{row.rank}
            </div>
            <div className="w-[100px] h-[37px] flex-shrink-0 bg-[#2a475e]" />
            <div className="flex-1 min-w-0 px-2 text-white font-medium text-[13px] truncate">
              {row.name}
            </div>
            <div className="w-[64px] flex-shrink-0 px-2 text-right text-[#8f98a0] text-[11px]">
              {row.release}
            </div>
            <div className="w-[64px] flex-shrink-0 px-2 text-right text-[#a4d007] text-[13px]">
              {row.ccu}
            </div>
            <div className="w-[64px] flex-shrink-0 px-2 text-right text-[#66c0f4] text-[13px]">
              {row.top}
            </div>
            <div className="w-[64px] flex-shrink-0 px-2 text-right text-[#c7d5e0] text-xs">
              {row.price}
            </div>
            <div className="w-[64px] flex-shrink-0" />
          </div>
        ))}

        {/* Mobile */}
        <div className="md:hidden divide-y divide-[#2a475e]">
          {FAKE_ROWS.slice(0, 6).map((row) => (
            <div key={row.rank} className="px-3 py-2 space-y-1.5">
              <div className="flex items-center gap-3">
                <div className="w-[100px] h-[37px] flex-shrink-0 bg-[#2a475e]" />
                <div className="flex-1 min-w-0">
                  <span className="text-white font-medium text-[13px] block truncate">{row.name}</span>
                  <span className="text-[#8f98a0] text-[11px]">{row.release}</span>
                </div>
              </div>
              <div className="flex justify-between text-xs text-[#8f98a0]">
                <span>CCU: <span className="text-[#a4d007] font-bold">{row.ccu}</span></span>
                <span>{row.price}</span>
              </div>
            </div>
          ))}
        </div>

        <FakeDataWatermark />
      </div>

      <LockedOverlay message={message} surface={surface} />
    </div>
  );
}
